interface ErrorFallbackProps {
  error: Error
  onReset: () => void
}

/**
 * Branded full-page error fallback rendered by ErrorBoundary when a render
 * error escapes. Matches the bare-auth-page style used by AuthActionPage,
 * LoginPage, and AuthConfirmPage.
 *
 * Deliberately does NOT show error.message or stack trace to the user —
 * that data flows to Sentry, not the screen. The `error` prop is part of
 * the contract for callers but intentionally unused in the body.
 */
export function ErrorFallback({ onReset }: ErrorFallbackProps) {
  return (
    <div className="min-h-screen bg-navy flex flex-col">
      <div className="px-6 py-6">
        <a
          href="/"
          className="font-heading text-gold text-xl font-semibold"
        >
          Advantage Horsemanship
        </a>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md text-center">
          <h1 className="font-heading text-4xl sm:text-5xl text-white font-semibold mb-4">
            Something went wrong
          </h1>
          <p className="font-body text-base text-white/70 leading-relaxed mb-8">
            We hit an unexpected problem loading this page. Please try again,
            or return to the homepage.
          </p>

          <div className="flex flex-col">
            <button
              type="button"
              onClick={onReset}
              className="w-full font-body text-sm font-semibold bg-gold text-navy px-8 py-4 min-h-[52px] inline-flex items-center justify-center hover:opacity-90 transition-opacity duration-150 mb-3"
            >
              Try Again
            </button>
            <a
              href="/"
              className="w-full font-body text-sm font-semibold bg-transparent text-white border border-white/30 px-8 py-4 min-h-[52px] inline-flex items-center justify-center hover:bg-white/10 transition-colors duration-150"
            >
              Back to Home
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
